import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PASSWORD_MIN_LENGTH } from '@bingo/shared';
import { ApiError, api } from '../../lib/api.js';
import { useSession } from '../../lib/useSession.js';

/**
 * /minha-conta — nome e senha.
 *
 * Trocar a senha exige a **senha atual**: sem isso, um token roubado
 * trocaria a senha e trancaria o dono para fora da própria conta. E a troca
 * **desconecta os outros aparelhos**, porque quem troca a senha normalmente
 * está expulsando alguém.
 */
export function MinhaConta() {
  const session = useSession();

  const [name, setName] = useState(session.user?.name ?? '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const [nameMessage, setNameMessage] = useState<string | null>(null);
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const salvarNome = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setNameError(null);
    setNameMessage(null);

    try {
      await api('/api/me', { method: 'PATCH', body: { name: name.trim() } });
      setNameMessage('Nome atualizado.');
    } catch (err) {
      setNameError(err instanceof ApiError ? err.message : 'Não foi possível salvar.');
    } finally {
      setBusy(false);
    }
  };

  const trocarSenha = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setPasswordError(null);
    setPasswordMessage(null);

    try {
      await api('/api/me/password', { method: 'POST', body: { currentPassword, newPassword } });
      setCurrentPassword('');
      setNewPassword('');
      setPasswordMessage('Senha trocada. Os outros aparelhos foram desconectados.');
    } catch (err) {
      setPasswordError(err instanceof ApiError ? err.message : 'Não foi possível trocar a senha.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="shell">
      <header className="shell-head">
        <div>
          <h1>Minha conta</h1>
          <p className="hint-text">{session.user?.email}</p>
        </div>
        <Link className="btn" to="/inicio">
          Meus jogos
        </Link>
      </header>

      <section>
        <h2>Nome</h2>
        <form className="form" onSubmit={(event) => void salvarNome(event)}>
          <label className="field">
            <span>Como você aparece</span>
            <input value={name} onChange={(event) => setName(event.target.value)} required />
          </label>

          {nameError ? (
            <p className="error" role="alert">
              {nameError}
            </p>
          ) : null}
          {nameMessage ? <p className="hint-text">{nameMessage}</p> : null}

          <button type="submit" className="btn primary block" disabled={busy}>
            Salvar nome
          </button>
        </form>
      </section>

      <section>
        <h2>Senha</h2>
        <form className="form" onSubmit={(event) => void trocarSenha(event)}>
          <label className="field">
            <span>Senha atual</span>
            <input
              type="password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              autoComplete="current-password"
              required
            />
          </label>

          <label className="field">
            <span>Senha nova</span>
            <input
              type="password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              autoComplete="new-password"
              minLength={PASSWORD_MIN_LENGTH}
              required
            />
          </label>
          <p className="hint-text">
            Pelo menos {PASSWORD_MIN_LENGTH} caracteres. Os outros aparelhos onde esta conta está
            aberta serão desconectados.
          </p>

          {passwordError ? (
            <p className="error" role="alert">
              {passwordError}
            </p>
          ) : null}
          {passwordMessage ? <p className="hint-text">{passwordMessage}</p> : null}

          <button type="submit" className="btn primary block" disabled={busy}>
            Trocar senha
          </button>
        </form>
      </section>
    </main>
  );
}
